import '../styles/TrackShipmentCustodyHistory.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const CUSTODY_COLLECTION = 'custody_events';

const STAGE_ORDER = ['Picked up', 'In transit', 'Customs', 'Out for delivery', 'Delivered'];

function stageRank(stage) {
  const idx = STAGE_ORDER.indexOf(stage);
  return idx === -1 ? STAGE_ORDER.length : idx;
}

function TrackShipmentCustodyHistory({ shipmentId = '' }) {
  const { data, loading, error, refresh } = useProjectCollection(CUSTODY_COLLECTION);
  const [retrying, setRetrying] = useState(false);

  const allEvents = Array.isArray(data) ? data : [];

  const events = useMemo(() => {
    if (!shipmentId) return [];
    return allEvents
      .filter((event) => event && event.shipmentId === shipmentId)
      .slice()
      .sort((a, b) => {
        const byStage = stageRank(a.stage) - stageRank(b.stage);
        if (byStage !== 0) return byStage;
        return String(a.timestamp || '').localeCompare(String(b.timestamp || ''));
      });
  }, [allEvents, shipmentId]);

  useEffect(() => {
    setRetrying(false);
  }, [shipmentId, error]);

  const hasShipment = Boolean(shipmentId);
  const loadFailed = Boolean(error);

  async function handleRetry() {
    if (typeof refresh !== 'function') return;
    setRetrying(true);
    try {
      await refresh();
    } finally {
      setRetrying(false);
    }
  }

  const footerLabel = !hasShipment
    ? 'Awaiting a resolved waybill'
    : loadFailed
      ? 'History unavailable'
      : events.length === 1
        ? '1 custody event recorded'
        : `${events.length} custody events recorded`;

  return (
    <section className="tch-root" aria-labelledby="tch-title">
      <div className="tch-shell">
        <header className="tch-head">
          <div>
            <span className="tch-eyebrow">Chain of custody</span>
            <h2 className="tch-title" id="tch-title">
              Custody History
            </h2>
          </div>
          {hasShipment && (
            <span className="tch-meta">Waybill {shipmentId}</span>
          )}
        </header>

        <div className="tch-rule" aria-hidden="true" />

        {!hasShipment && (
          <div className="tch-note" role="status">
            <span className="tch-note-label">No shipment resolved</span>
            <p className="tch-note-text">
              Custody events appear here once a waybill has been resolved in the lookup above.
              Enter a waybill number to read the chain from handover to delivery.
            </p>
          </div>
        )}

        {hasShipment && loadFailed && (
          <div className="tch-note tch-note--error" role="alert">
            <span className="tch-note-label">History failed to load</span>
            <p className="tch-note-text">
              The custody events for waybill {shipmentId} could not be read. The gauge and route
              above stay as they were; retry to read the custody chain again.
            </p>
            <button
              type="button"
              className="tch-retry"
              onClick={handleRetry}
              disabled={retrying}
            >
              {retrying ? 'Retrying…' : 'Retry history'}
            </button>
          </div>
        )}

        {hasShipment && !loadFailed && loading && (
          <div className="tch-note" role="status">
            <span className="tch-note-label">Reading custody chain</span>
            <p className="tch-note-text">Loading the recorded custody events for waybill {shipmentId}.</p>
          </div>
        )}

        {hasShipment && !loadFailed && !loading && events.length === 0 && (
          <div className="tch-note" role="status">
            <span className="tch-note-label">No custody events recorded</span>
            <p className="tch-note-text">
              No custody events have been recorded for waybill {shipmentId} yet.
            </p>
          </div>
        )}

        {hasShipment && !loadFailed && !loading && events.length > 0 && (
          <>
            <ol className="tch-list">
              {events.map((event) => {
                const delivered = event.stage === 'Delivered';
                return (
                  <li
                    key={event.id}
                    className={'tch-row' + (delivered ? ' is-delivered' : '')}
                  >
                    <span className="tch-stage">
                      {delivered ? (
                        <span className="tch-dot" aria-hidden="true" />
                      ) : (
                        <span className="tch-index" aria-hidden="true">
                          ▸
                        </span>
                      )}
                      <span className="tch-stage-label">{event.stage}</span>
                      {delivered && <span className="tch-actor">Verified</span>}
                    </span>
                    <span className="tch-actor">{event.actor}</span>
                    <span className="tch-time">
                      <span className="tch-time-label">Recorded</span>
                      <span className="tch-stamp">{event.timestamp}</span>
                    </span>
                  </li>
                );
              })}
            </ol>

            <div className="tch-baseline">
              <span>{footerLabel}</span>
              <span className="tch-count-value">
                {events[0].stage} → {events[events.length - 1].stage}
              </span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default TrackShipmentCustodyHistory;